import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { Assert } from '../../assert';
import { useTranslations } from '../../translations/composables/useTranslations';
import { KeyboardActions } from '../domain/keyboardActions';

export interface KeyPress {
  key: string | undefined;
  code: string | undefined;
  altKey: boolean | undefined;
  ctrlKey: boolean | undefined;
  shiftKey: boolean | undefined;
  metaKey: boolean | undefined;
  timestamp: number;
}

export interface Keybind {
  key: string | undefined;
  code: string | undefined;
  altKey: boolean | undefined;
  ctrlKey: boolean | undefined;
  shiftKey: boolean | undefined;
  metaKey: boolean | undefined;
}

interface FormKeybind extends Keybind {
  actionText: string;
  keyText: string;
}

interface FormKeyboardAction {
  action: KeyboardActions;
  keybind: FormKeybind;
  changed: boolean;
}

interface KeyboardAction {
  action: KeyboardActions | null;
  timestamp: number;
}

export const useKeybindStore = defineStore('keybinds', () => {
  const translation = useTranslations();

  const defaultBindings: Record<KeyboardActions, Keybind> = {
    [KeyboardActions.PickFromLogYard]: {
      key: '6',
      code: '',
      ctrlKey: false,
      altKey: false,
      shiftKey: false,
      metaKey: false,
    },
    [KeyboardActions.LeaveInLogYard]: {
      key: '7',
      code: '',
      ctrlKey: false,
      altKey: false,
      shiftKey: false,
      metaKey: false,
    },
  };

  const mode = ref<'default' | 'edit' | 'edge' | 'none'>('none');
  const formData = ref<FormKeyboardAction[]>([]);
  const isLockedInitial = ref<boolean>(false);
  const isLocked = ref<boolean>(false);

  const keyboardAction = ref<KeyboardAction>({ action: null, timestamp: 0 });
  const bindings = ref<Record<KeyboardActions, Keybind>>();

  const keybindings = computed(() => {
    if (bindings.value) {
      return typeSafeObjectEntries(bindings.value);
    }
    return undefined;
  });

  function parseKeybindings(
    keybindString: string,
    locked: boolean,
    operation: 'default' | 'edit' | 'edge',
  ) {
    mode.value = operation;
    if ((operation === 'edit' || operation === 'edge') && keybindString.length > 0) {
      try {
        let result = JSON.parse(keybindString) as Record<KeyboardActions, Keybind>;
        result = syncRecordWithEnum(result);

        bindings.value = result;
        isLockedInitial.value = locked;
        isLocked.value = locked;

        updateFormData();
        return;
      } catch (e) {
        console.error(e);
      }
    }
    bindings.value = defaultBindings;
    isLockedInitial.value = false;
    isLocked.value = false;
    updateFormData();
  }

  function keyboardHandler(event: KeyboardEvent) {
    if (
      event.target != window.document.body &&
      event.target != window.document.getElementsByClassName('maplibregl-canvas')[0]
    )
      return;
    keyboardAction.value = {
      action: findKeyboardAction(event.key),
      timestamp: Date.now(),
    };
  }

  function findKeyboardAction(keyCode: string): KeyboardActions | null {
    if (!keybindings.value) return null;
    const action = keybindings.value.find((x) => x[1].key === keyCode);

    if (action) {
      return action[0];
    } else {
      return null;
    }
  }

  function editKeybind(index: number, e: KeyboardEvent) {
    e.preventDefault();
    e.stopPropagation();
    let keyModel = '';
    if (e.ctrlKey && e.key != 'Control') keyModel += 'Ctrl+';
    if (e.altKey && e.key != 'Alt') keyModel += 'Alt+';
    if (e.shiftKey && e.key != 'Shift') keyModel += 'Shift+';
    if (e.metaKey && e.key != 'Meta') keyModel += 'Meta+';

    keyModel += e.key;

    if (formData.value[index]) {
      const keybind = formData.value[index].keybind;
      keybind.keyText = keyModel.toUpperCase();
      keybind.key = e.key;
      keybind.code = e.code;
      keybind.ctrlKey = e.ctrlKey;
      keybind.altKey = e.altKey;
      keybind.shiftKey = e.shiftKey;
      keybind.metaKey = e.metaKey;

      formData.value[index].changed = isChanged(formData.value[index]);
    }
  }

  function stringifyFormData() {
    const keybinds: Record<KeyboardActions, Keybind> = formData.value.reduce(
      (acc, d) => {
        acc[d.action] = toKeybind(d.keybind);
        return acc;
      },
      {} as Record<KeyboardActions, Keybind>,
    );

    return JSON.stringify(keybinds);
  }

  function toKeybind(formKeybind: FormKeybind): Keybind {
    const { actionText, keyText, ...keybind } = formKeybind;
    return keybind;
  }

  function isChanged(formAction: FormKeyboardAction) {
    if (!keybindings.value) return false;
    if (mode.value === 'default') return false;
    const keybind = formAction.keybind;
    const savedKeybind = keybindings.value.find((action) => action[0] === formAction.action);
    Assert.definied(savedKeybind);

    if (savedKeybind[1].key !== keybind.key) return true;
    if (savedKeybind[1].code !== keybind.code) return true;
    if (savedKeybind[1].ctrlKey !== keybind.ctrlKey) return true;
    if (savedKeybind[1].altKey !== keybind.altKey) return true;
    if (savedKeybind[1].shiftKey !== keybind.shiftKey) return true;
    if (savedKeybind[1].metaKey !== keybind.metaKey) return true;

    return false;
  }

  const isFormChanged = computed(() => {
    if (mode.value == 'default') return false;
    if (isLocked.value !== isLockedInitial.value) return true;
    for (const entry of formData.value) {
      if (entry.changed) {
        return true;
      }
    }
    return false;
  });

  const typeSafeObjectEntries = <T extends Record<PropertyKey, unknown>>(
    obj: T,
  ): { [K in keyof T]: [K, T[K]] }[keyof T][] => {
    return Object.entries(obj) as { [K in keyof T]: [K, T[K]] }[keyof T][];
  };

  function updateFormData() {
    if (!keybindings.value) return;
    formData.value = [];
    for (const [keyboardAction, keybind] of keybindings.value) {
      let keyModel = '';
      if (keybind.ctrlKey && keybind.key != 'Control') keyModel += 'Ctrl+';
      if (keybind.altKey && keybind.key != 'Alt') keyModel += 'Alt+';
      if (keybind.shiftKey && keybind.key != 'Shift') keyModel += 'Shift+';
      if (keybind.metaKey && keybind.key != 'Meta') keyModel += 'Meta+';

      keyModel += keybind.key;

      formData.value.push({
        action: keyboardAction,
        keybind: {
          actionText: translation.value.enums.keyboardAction(keyboardAction),
          keyText: keyModel.toUpperCase() ?? '',
          key: keybind.key,
          code: keybind.code,
          ctrlKey: keybind.ctrlKey,
          altKey: keybind.altKey,
          shiftKey: keybind.shiftKey,
          metaKey: keybind.metaKey,
        },
        changed: false,
      });
    }
  }

  // Adds or remove actions that's not part of the enum.
  function syncRecordWithEnum(
    record: Partial<Record<KeyboardActions, Keybind>>,
  ): Record<KeyboardActions, Keybind> {
    const result = {} as Record<KeyboardActions, Keybind>;

    for (const action of Object.values(KeyboardActions)) {
      const fromRecord = record[action];

      // Only keep values that belong to the enum
      if (fromRecord) {
        result[action] = fromRecord;
      } else {
        result[action] = JSON.parse(JSON.stringify(defaultBindings[action]));
      }
    }

    return result;
  }

  return {
    keyboardHandler,
    parseKeybindings,
    editKeybind,
    stringifyFormData,
    updateFormData,
    keyboardAction,
    keybindings,
    bindings,
    isLocked,
    formData,
    isFormChanged,
  };
});
